/**
 * Runtime configuration loader (API-served).
 *
 * The admin-web SPA is built ONCE and promoted across environments. Runtime
 * values (Stripe publishable key, SSO login origin) are therefore decided by
 * the API, not baked into the image at build time.
 *
 * At boot (`main.tsx`) we fetch `${API_BASE}/ui-config` and copy the result
 * into `window.__SPS_*` globals BEFORE the React app modules are imported.
 * `runtime-config.ts` then reads those globals synchronously.
 *
 * Precedence: a reachable API response is authoritative. If the fetch fails
 * (e.g. local `vite dev` with no API, or a transient error) we leave whatever
 * `public/config.js` / build-time `VITE_*` already set.
 */

interface UiConfigResponse {
  stripePublishableKey?: string;
  ssoWebBaseUrl?: string;
  authRedirectUri?: string | null;
  sentryDsn?: string;
  sentryEnvironment?: string;
  environment?: string;
}

interface RuntimeWindow {
  __SPS_API_BASE?: string;
  __SPS_STRIPE_PUBLISHABLE_KEY?: string;
  __SPS_SSO_WEB_BASE_URL?: string;
  __SPS_AUTH_REDIRECT_URI?: string;
  __SPS_SENTRY_DSN?: string;
  __SPS_SENTRY_ENVIRONMENT?: string;
  __SPS_ENVIRONMENT?: string;
}

/**
 * Fetch the UI runtime config from the API and populate `window.__SPS_*`.
 * Always resolves (never throws) so it is safe to `await` during boot.
 */
export async function resolveRuntimeConfig(): Promise<void> {
  if (typeof window === "undefined") {
    return;
  }

  const win = window as unknown as RuntimeWindow;
  const buildEnv = (import.meta as { env: Record<string, string | undefined> }).env;
  const base = win.__SPS_API_BASE ?? buildEnv["VITE_SPS_API_BASE"] ?? "/api";

  try {
    const res = await fetch(`${base}/ui-config`, {
      credentials: "include",
      headers: { accept: "application/json" },
    });
    if (!res.ok) {
      return;
    }

    const cfg = (await res.json()) as UiConfigResponse;

    if (cfg.stripePublishableKey) {
      win.__SPS_STRIPE_PUBLISHABLE_KEY = cfg.stripePublishableKey;
    }
    if (cfg.ssoWebBaseUrl) {
      win.__SPS_SSO_WEB_BASE_URL = cfg.ssoWebBaseUrl;
    }
    if (cfg.authRedirectUri) {
      win.__SPS_AUTH_REDIRECT_URI = cfg.authRedirectUri;
    }
    if (cfg.sentryDsn) {
      win.__SPS_SENTRY_DSN = cfg.sentryDsn;
    }
    if (cfg.sentryEnvironment) {
      win.__SPS_SENTRY_ENVIRONMENT = cfg.sentryEnvironment;
    }
    if (cfg.environment) {
      win.__SPS_ENVIRONMENT = cfg.environment;
    }
  } catch {
    // Network/parse failure: keep config.js / build-time fallback.
  }
}
